import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import FlagIcon from '../FlagIcon';

const countryFlagClass = 'h-3.5 w-[18px] shrink-0 rounded-[2px]';

function SearchableSelect({
  id,
  name,
  value,
  onChange,
  placeholder,
  required,
  options,
  className = '',
  label,
  renderSelected,
  renderOption,
  hasError,
  clearable,
  selectedMultiline,
  hideValue,
  disabled = false,
  menuClassName = '',
  menuMinWidth,
  menuAlign = 'left',
  searchPlaceholder = 'Search currency or country',
  menuMaxHeight = 360,
  menuPlacement = 'down',
  triggerClassName = '',
  showCountryFlags = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [resolvedPlacement, setResolvedPlacement] = useState('down');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const triggerRef = useRef(null);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setSearchTerm('');
  }, []);

  const removeDuplicates = useCallback((optionsList) => {
    if (!optionsList || optionsList.length === 0) return [];

    const seen = new Set();
    return optionsList.filter((option) => {
      if (option === null || option === undefined) return false;

      let key;

      if (typeof option === 'object') {
        const optionValue = option.value !== null && option.value !== undefined ? String(option.value) : '';
        const optionLabel = option.label !== null && option.label !== undefined ? String(option.label) : '';

        const normalizedValue = optionValue.toLowerCase().trim();
        const normalizedLabel = optionLabel.toLowerCase().trim();

        key = normalizedValue || normalizedLabel;
      } else {
        key = String(option).toLowerCase().trim();
      }

      if (!key) return false;

      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
  }, []);

  useEffect(() => {
    const uniqueOptions = removeDuplicates(options || []);

    if (!searchTerm) {
      setFilteredOptions(uniqueOptions);
    } else {
      const searchLower = searchTerm.toLowerCase().trim();
      const scored = uniqueOptions
        .map((option) => {
          const optionLabel = String(
            typeof option === 'object' && option.label != null ? option.label : option ?? '',
          ).toLowerCase();
          const optionValue = String(
            typeof option === 'object' && option.value != null ? option.value : option ?? '',
          ).toLowerCase();
          const optionSearchText = String(
            typeof option === 'object' && option.searchText != null ? option.searchText : '',
          ).toLowerCase();
          const displayText =
            optionValue && optionLabel !== optionValue ? `${optionLabel} (${optionValue})` : optionLabel;

          const valueExact = optionValue === searchLower;
          const valuePrefix = Boolean(optionValue) && optionValue.startsWith(searchLower);
          const labelMatch = optionLabel.includes(searchLower);
          const searchTextMatch = optionSearchText.includes(searchLower);
          const displayMatch = displayText.includes(searchLower);

          if (!valueExact && !valuePrefix && !labelMatch && !searchTextMatch && !displayMatch) {
            return null;
          }

          let score = 4;
          if (valueExact) score = 0;
          else if (valuePrefix) score = 1;
          else if (optionLabel.startsWith(searchLower)) score = 2;
          else if (labelMatch || displayMatch) score = 3;

          return { option, score, optionLabel };
        })
        .filter(Boolean)
        .sort((a, b) => a.score - b.score || a.optionLabel.localeCompare(b.optionLabel));

      setFilteredOptions(scored.map((item) => item.option));
    }
  }, [searchTerm, options, removeDuplicates]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [closeMenu]);

  useEffect(() => {
    if (!isOpen || !dropdownRef.current) return undefined;

    const updatePlacement = () => {
      if (menuPlacement === 'up' || menuPlacement === 'down') {
        setResolvedPlacement(menuPlacement);
        return;
      }

      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const needed = Math.min(menuMaxHeight, 280) + 12;
      setResolvedPlacement(spaceBelow < needed && spaceAbove > spaceBelow ? 'up' : 'down');
    };

    updatePlacement();
    window.addEventListener('resize', updatePlacement);
    window.addEventListener('scroll', updatePlacement, true);
    return () => {
      window.removeEventListener('resize', updatePlacement);
      window.removeEventListener('scroll', updatePlacement, true);
    };
  }, [isOpen, menuMaxHeight, menuPlacement]);

  const handleToggle = () => {
    if (disabled) return;
    if (isOpen) {
      closeMenu();
      return;
    }
    setIsOpen(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const handleTriggerKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
      }
    } else if (e.key === 'Escape' && isOpen) {
      e.preventDefault();
      closeMenu();
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeMenu();
      triggerRef.current?.focus();
      return;
    }
    if (e.key === 'Tab') {
      closeMenu();
    }
  };

  const handleOptionSelect = (option) => {
    const optionValue = typeof option === 'object' && option.value != null ? option.value : option;

    const syntheticEvent = {
      target: {
        name,
        value: optionValue,
      },
    };

    onChange(syntheticEvent);
    closeMenu();
    triggerRef.current?.focus();
  };

  const getSelectedOption = () => {
    if (value == null || value === '') return null;
    const valueTrimmed = String(value).trim();
    if (!valueTrimmed) return null;
    return (
      (options || []).find((option) => {
        const optionValue = typeof option === 'object' && option.value != null ? option.value : option;
        return String(optionValue).trim() === valueTrimmed;
      }) || null
    );
  };

  const getSelectedLabel = () => {
    const selectedOption = getSelectedOption();
    if (selectedOption) {
      if (typeof selectedOption === 'object') {
        const optVal = selectedOption.value != null ? selectedOption.value : '';
        const optLabel = selectedOption.label != null ? selectedOption.label : '';
        if (!hideValue && optLabel && String(optLabel).trim() !== String(optVal).trim()) {
          return `${optLabel} (${optVal})`;
        }
        return optLabel || optVal || '';
      }
      return selectedOption;
    }
    if (value != null && String(value).trim()) {
      return String(value).trim();
    }
    return '';
  };

  const handleClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onChange({ target: { name, value: '' } });
    closeMenu();
    triggerRef.current?.focus();
  };

  const getRenderSelectedHelpers = () => {
    if (!selectedMultiline || value == null || !String(value).trim()) return undefined;
    const codes = String(value)
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean);
    if (codes.length <= 1) return undefined;
    return {
      removeValue: (code) => {
        const next = codes.filter((c) => c !== code).join(', ');
        onChange(next);
      },
    };
  };

  const defaultTriggerClassName = `flex w-full rounded-md border px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
    selectedMultiline ? 'min-h-10 items-start' : 'h-10 items-center'
  } gap-2 ${
    hasError ? '!border-red-500 border-2 bg-background' : 'border border-input bg-background'
  } ${isOpen ? 'ring-2 ring-ring ring-offset-2' : ''}`;

  const resolvedTriggerClassName = triggerClassName?.includes('w-full')
    ? triggerClassName
    : `${triggerClassName || defaultTriggerClassName} w-full`;

  return (
    <div className={`relative w-full ${isOpen ? 'z-50' : 'z-0'} ${className}`.trim()}>
      {label && (
        <label htmlFor={id} className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'>
          {label} {required && '*'}
        </label>
      )}
      <div className={`relative w-full ${label ? 'mt-2' : ''}`} ref={dropdownRef}>
        <button
          type='button'
          id={id}
          ref={triggerRef}
          onClick={handleToggle}
          onKeyDown={handleTriggerKeyDown}
          className={resolvedTriggerClassName}
          aria-expanded={isOpen}
          aria-haspopup='listbox'
          disabled={disabled}
        >
          <span
            className={`flex min-w-0 flex-1 text-left ${selectedMultiline ? 'flex-wrap gap-2' : 'truncate items-center gap-2'} ${value && String(value).trim() ? 'text-[#0B1929]' : 'text-[#7A9AB0]'}`}
          >
            {(() => {
              if (renderSelected && value != null && String(value).trim()) {
                return renderSelected(value, getSelectedOption(), getRenderSelectedHelpers());
              }
              if (showCountryFlags && value != null && String(value).trim()) {
                return (
                  <>
                    <FlagIcon countryCode={String(value).trim().toUpperCase()} className={countryFlagClass} />
                    <span className='truncate'>{getSelectedLabel()}</span>
                  </>
                );
              }
              return getSelectedLabel() || placeholder;
            })()}
          </span>
          <span className='flex shrink-0 items-center gap-1'>
            {clearable && value != null && String(value).trim() && (
              <span
                role='button'
                tabIndex={0}
                onClick={handleClear}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleClear(e);
                  }
                }}
                className='cursor-pointer rounded p-0.5 hover:bg-[#EBF4FB] focus:outline-none focus:ring-2 focus:ring-[#2E6BAD]/30'
                aria-label='Clear selection'
              >
                <svg className='h-4 w-4 text-[#7A9AB0]' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                </svg>
              </span>
            )}
            <svg
              className={`h-4 w-4 shrink-0 text-[#7A9AB0] transition-transform ${isOpen ? 'rotate-180' : ''}`}
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
            </svg>
          </span>
        </button>

        {isOpen && (
          <div
            className={`absolute left-0 right-0 z-[1000] flex w-full flex-col overflow-hidden rounded-[10px] border-[1.5px] border-[#DDE6EF] bg-white shadow-[0_8px_24px_rgba(11,25,41,0.14)] ${
              resolvedPlacement === 'up' ? 'bottom-full mb-2' : 'top-full mt-2'
            } ${menuAlign === 'right' ? 'right-0 left-auto' : ''} ${menuMinWidth || ''} ${menuClassName}`}
            style={{ maxHeight: menuMaxHeight }}
          >
            <div className='shrink-0 border-b border-[#EEF3F8] bg-white p-2'>
              <input
                ref={inputRef}
                type='text'
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className='w-full rounded-md border border-[#DDE6EF] bg-[#F8FAFB] px-[10px] py-[7px] text-[12.5px] font-medium text-[#0B1929] outline-none placeholder:text-[#7A9AB0] focus:border-[#2E6BAD]'
              />
            </div>
            <div className='overflow-auto bg-white p-1' style={{ maxHeight: Math.max(menuMaxHeight - 52, 120) }}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => {
                  const optionValue = typeof option === 'object' && option.value != null ? option.value : option;
                  const optionLabel = typeof option === 'object' && option.label ? option.label : option;
                  const isSelected = value != null && String(value).trim() === String(optionValue).trim();
                  let displayText = optionLabel;
                  if (typeof option === 'object' && option.value != null && option.label != null) {
                    if (String(option.label).toLowerCase() === String(option.value).toLowerCase()) {
                      displayText = option.label;
                    } else if (!hideValue) {
                      displayText = `${option.label} (${option.value})`;
                    } else {
                      displayText = option.label;
                    }
                  }

                  const uniqueKey = `${String(optionValue).toLowerCase()}-${index}`;

                  return (
                    <button
                      key={uniqueKey}
                      type='button'
                      tabIndex={-1}
                      onClick={() => handleOptionSelect(option)}
                      className={`flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-left text-[13px] font-medium text-[#0B1929] hover:bg-[#EBF4FB] ${
                        isSelected ? 'bg-[#EBF4FB]' : 'bg-white'
                      }`}
                    >
                      {showCountryFlags && !renderOption && (
                        <FlagIcon countryCode={String(optionValue).trim().toUpperCase()} className={countryFlagClass} />
                      )}
                      {renderOption ? renderOption(option) : displayText}
                    </button>
                  );
                })
              ) : (
                <div className='px-3 py-2 text-sm text-[#7A9AB0]'>No options found</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

SearchableSelect.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
  className: PropTypes.string,
  label: PropTypes.string,
  renderSelected: PropTypes.func,
  renderOption: PropTypes.func,
  hasError: PropTypes.bool,
  clearable: PropTypes.bool,
  selectedMultiline: PropTypes.bool,
  hideValue: PropTypes.bool,
  disabled: PropTypes.bool,
  menuClassName: PropTypes.string,
  menuMinWidth: PropTypes.string,
  menuAlign: PropTypes.oneOf(['left', 'right']),
  searchPlaceholder: PropTypes.string,
  menuMaxHeight: PropTypes.number,
  menuPlacement: PropTypes.oneOf(['down', 'up', 'auto']),
  triggerClassName: PropTypes.string,
  showCountryFlags: PropTypes.bool,
};

export default SearchableSelect;
